import { useState } from 'react'
import NewEliteLandingPlanSelector from '../../new-elite-landing/components/NewEliteLandingPlanSelector'
import { ELiteMembershipStatus } from '../../../types/mutual-fund-detail.types'
import { EliteLandingPageState } from '../../../types/misc.types'
import { useEliteOnboardingAdvisoryStatus } from '../../new-elite-landing/hooks/useEliteOnboardingAdvisoryStatus'
import { useSyncEliteLandingPlanFromEligiblePlans } from '../../new-elite-landing/hooks/useSyncEliteLandingPlanFromEligiblePlans'
import { getPlanSelectorEliteLandingPageState } from '../../new-elite-landing/eliteLandingPlanSelectorState'
import {
  isEliteAutoPayActive,
  shouldShowEliteRenewalPrompt
} from '../../../components/elite/dashboard/eliteMembershipRenewalWindow'
import type { EliteLandingPlan } from '../../new-elite-landing/planTypes'
import { buildFamilyOnboardingViewProps } from '../../new-elite-landing/pages/family/familyOnboardingViewProps'
import { NewEliteLandingAnalyticsProvider } from '../../new-elite-landing/analytics/NewEliteLandingAnalyticsContext'
import {
  NewEliteLandingAnalyticsScreen,
  type NewEliteLandingAnalyticsScreenType
} from '../../new-elite-landing/analytics/newEliteLandingAnalytics'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { ELITE_PLANS_MEMBER_ELITE_STATUS_QUERY_KEY } from '../constants/queryKeys'
import { useTrackElitePlansScreenLoadWhenReady } from '../hooks/useTrackElitePlansScreenLoadWhenReady'
import { ElitePlansHeroCurve } from '../icons/ElitePlansHeroCurve'
import {
  ElitePlansHeroEmblem,
  ElitePlansHeroBadge,
  ElitePlansHeroHeadline
} from './hero/ElitePlansHeroHeading'
import { ElitePlansEliteMembershipStatus } from './ElitePlansEliteMembershipStatus'
import { ElitePlansDashboardBelowFold } from './ElitePlansDashboardBelowFold'

type ElitePlansEliteMemberDashboardProps = {
  /** Override advisory status cache key (family member route uses its own root). */
  statusQueryKeyRoot?: string
  analyticsScreen?: NewEliteLandingAnalyticsScreenType
}

export function ElitePlansEliteMemberDashboard({
  statusQueryKeyRoot = ELITE_PLANS_MEMBER_ELITE_STATUS_QUERY_KEY,
  analyticsScreen = NewEliteLandingAnalyticsScreen.Dashboard
}: ElitePlansEliteMemberDashboardProps) {
  return (
    <NewEliteLandingAnalyticsProvider screen={analyticsScreen}>
      <ElitePlansEliteMemberDashboardContent statusQueryKeyRoot={statusQueryKeyRoot} />
    </NewEliteLandingAnalyticsProvider>
  )
}

function ElitePlansEliteMemberDashboardContent({
  statusQueryKeyRoot
}: {
  statusQueryKeyRoot: string
}) {
  const { activeTab } = useElitePlansLanding()
  const hero = activeTab.hero
  const [selectedPlan, setSelectedPlan] = useState<EliteLandingPlan>('yearly')
  const { data, isLoading } = useEliteOnboardingAdvisoryStatus(statusQueryKeyRoot)
  const familyConfig = buildFamilyOnboardingViewProps(data)
  const eligiblePlans = data?.eligiblePlans ?? []
  const isPlanExpired =
    data?.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED && !isEliteAutoPayActive(data)
  const isEliteMember = data?.subscriptionDetails?.status === ELiteMembershipStatus.ELITE
  const planSelectorLandingState = getPlanSelectorEliteLandingPageState(data?.eliteLandingPageState)
  const showRenewalPrompt = shouldShowEliteRenewalPrompt(data)
  const showPlanSelector =
    !isLoading && !showRenewalPrompt && (!isEliteMember || isPlanExpired)

  useSyncEliteLandingPlanFromEligiblePlans(eligiblePlans, setSelectedPlan, data?.selectedPlanDetails)
  // Route ScreenLoaded (Elite_Plans_Member / Family_Member); tier + from in eventId
  useTrackElitePlansScreenLoadWhenReady(!isLoading)

  return (
    <div>
      <section className='relative text-center'>
        <div className='ep-page-bg ep-theme-tween w-full'>
          <div className='relative mx-auto flex max-w-[720px] flex-col items-center px-5'>
            <ElitePlansHeroEmblem
              riveSrc={hero.emblemRiveSrc}
              fallbackSrc={hero.emblemSrc}
              alt={hero.emblemAlt ?? ''}
            />
            {hero.badge ? <ElitePlansHeroBadge>{hero.badge}</ElitePlansHeroBadge> : null}
            <ElitePlansHeroHeadline
              headlineSans={hero.headlineSans}
              headlineSerif={hero.headlineSerif}
            />
          </div>

          <div className='relative z-[1] mt-8 w-full'>
            <ElitePlansEliteMembershipStatus
              familyConfig={familyConfig}
              eliteOnboardingStatus={data}
              isStatusLoading={isLoading}
            />

            {showPlanSelector ? (
              <div className='w-full pb-4'>
                <NewEliteLandingPlanSelector
                  familyConfig={familyConfig}
                  selectedPlan={selectedPlan}
                  onPlanChange={setSelectedPlan}
                  eligiblePlans={eligiblePlans}
                  cta={data?.cta}
                  eliteLandingPageState={
                    isPlanExpired ? data?.eliteLandingPageState : planSelectorLandingState
                  }
                  selectedPlanDetails={data?.selectedPlanDetails}
                  activationExpiryDate={data?.subscriptionDetails?.activationExpiryDate}
                  autoPayStatus={data?.subscriptionDetails?.autoPayStatus}
                  renewalStatus={data?.renewal?.status}
                  consentText={data?.consentText}
                  consentTextList={data?.consentTextList}
                  isStatusLoading={isLoading}
                  referralReward={data?.referralReward}
                />
              </div>
            ) : null}
          </div>

          <ElitePlansHeroCurve themeId={activeTab.themeId} />
        </div>
      </section>

      <ElitePlansDashboardBelowFold
        isContentLoading={isLoading}
        showTestimonials
      />
    </div>
  )
}
